// The diff box — t-159 §3 (in the modal), §5 (in the banner, with a cancel per
// row), §6 (muted, listing what a hand-made save drops) and §8 (muted, with the
// row that refused highlighted).

import { ArrowRight, X } from "lucide-react";

import type { DiffGroup } from "./pendingDiff";
import styles from "./pending.module.css";

export interface DiffBoxProps {
  groups: DiffGroup[];
  /**
   * `list` is the plain box a banner draws. `modal` drops the cancel column —
   * a dialog is a decision about the whole package, not a place to edit it.
   * `muted` greys the new values: those rows are not going to happen.
   */
  variant?: "list" | "modal" | "muted";
  /** Draws the × on every row. Absent, the column is not drawn at all. */
  onCancelField?: (field: string, label: string) => void;
  /** A cancel already in flight — every × in the box refuses further clicks so
     a double click cannot take two fields out of the package. */
  cancelBusy?: boolean;
  /** Counters in the group headers. The modal shows them; the banner does too,
     and the muted "dropped" list has one group and no count. */
  counts?: boolean;
  /** Group heading override — the collision dialog labels its single group
     "Dropped from the package" rather than by the form card. */
  groupLabel?: string;
}

export function DiffBox({
  groups,
  variant = "list",
  onCancelField,
  cancelBusy,
  counts = true,
  groupLabel,
}: DiffBoxProps) {
  const variantClass =
    variant === "modal" ? styles.modal : variant === "muted" ? styles.muted : "";
  return (
    <div className={`${styles.diffBox} ${variantClass}`}>
      {groups.map((group) => (
        <div key={group.section}>
          <div className={styles.diffGroup}>
            {groupLabel ?? group.section}
            {counts && !groupLabel && (
              <span className={styles.countBadge}>{group.rows.length}</span>
            )}
          </div>
          {group.rows.map((row) => (
            <div
              key={`${row.field}:${row.api}`}
              className={`${styles.diffRow} ${row.blame ? styles.blame : ""}`}
            >
              <div>
                <div className={styles.diffName}>{row.label}</div>
                <div className={styles.diffApi}>{row.api}</div>
              </div>
              <div className={styles.diffVals}>
                <span
                  className={`${styles.diffOld} ${
                    row.oldMissing ? styles.diffOldNone : ""
                  }`}
                >
                  {row.oldText}
                </span>
                <span className={styles.diffArrow} aria-hidden>
                  →
                </span>
                <span className={styles.diffNew}>{row.newText}</span>
                {row.note && <span className={styles.diffNote}>{row.note}</span>}
                {row.blame && (
                  <span className={styles.blameTag}>this is what refused</span>
                )}
              </div>
              {onCancelField && variant !== "modal" && (
                <button
                  type="button"
                  className={styles.diffDel}
                  disabled={cancelBusy}
                  aria-label={`Remove ${row.label} from the package`}
                  onClick={() => onCancelField(row.field, row.label)}
                >
                  <X size={13} aria-hidden />
                </button>
              )}
              {row.consequence && (
                <div className={styles.consequence}>
                  <ArrowRight size={12} aria-hidden />
                  <span>{row.consequence}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
